import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { Drawer, Stack, Typography } from "@mui/material";
import { Editor } from "@/components";
import { applyTemplateToMenu, updateMenu } from "./slice";
import { useAppDispatch } from "@/hooks";
import TemplateGallery from "@/features/template-gallery/TemplateGallery";
import { useFetchTemplateByIdQuery } from "@/features/templates/templatesApi";
import { Template } from "@/types";
import routes from "@/constants/routes";
import { useFetchMenuByIdQuery } from "@/features/organizations/api/menusApi";
import PageLoading from "@/components/page-loading/PageLoading";

const MenuEditor = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { organizationId, menuId } = useParams<{ organizationId: string; menuId: string }>();
  const [openDrawer, setOpenDrawer] = useState(false);

  const menuQuery = useFetchMenuByIdQuery(
    {
      organizationId: organizationId ?? skipToken,
      menuId: menuId ?? skipToken,
    },
    {
      skip: !menuId || !organizationId,
    }
  );

  const templateQuery = useFetchTemplateByIdQuery(menuQuery?.data?.template ?? skipToken, {
    skip: !menuQuery?.data?.template,
  });

  const saveMenu = (data: string) => {
    dispatch(updateMenu({ ...menuQuery?.data, data }));
  };

  const onSelectTemplate = (templateId: Template["id"]) => {
    dispatch(applyTemplateToMenu({ menuId: menuId ?? "", templateId }));
    setOpenDrawer(false);
  };

  const secondaryActions = useMemo(
    () => [
      {
        children: "Cambiar Plantilla",
        onClick: () => setOpenDrawer(true),
      },
      {
        children: "Reniciar Plantilla",
        onClick: async () => {
          try {
            await dispatch(
              applyTemplateToMenu({
                menuId: menuId ?? "",
                templateId: menuQuery?.data?.template,
              })
            );
          } catch (error) {
            console.error(error);
          }
        },
      },
    ],
    [dispatch, menuQuery?.data?.template, menuId]
  );

  if (menuQuery.isLoading || templateQuery.isLoading) {
    return <PageLoading />;
  }

  if (!menuQuery?.data) {
    return <h2>Menu no econtrado</h2>;
  }

  if (!menuQuery?.data && !templateQuery?.data) {
    return <h2>Plantilla no econtrada, elige una plantilla</h2>;
  }

  return (
    <>
      <Editor
        title={menuQuery?.data?.title}
        data={menuQuery?.data?.data ?? templateQuery?.data.data ?? ""}
        onSave={saveMenu}
        actions={secondaryActions}
        onClose={() => navigate(routes.organizations.menus.main)}
        menu={menuQuery?.data}
        template={templateQuery?.data}
      />

      <Drawer
        PaperProps={{
          sx: { width: "100%" },
        }}
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        anchor="right"
      >
        <Stack spacing={4} padding={4}>
          <Typography variant="h1">Plantillas Disponibles</Typography>
          <TemplateGallery onSelect={onSelectTemplate} type={"select"} />
        </Stack>
      </Drawer>
    </>
  );
};

export default MenuEditor;
